import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import adminApi, { apiError } from "@/lib/adminApi";
import { Panel, Skeleton, StatusBadge, T, btnPrimary, btnSecondary, inputCls, DeleteButton } from "@/components/admin/ui";

const STATUS_LABELS = { active: "Actif", available: "Disponible", busy: "Occupé", offline: "Hors service", suspended: "Suspendu" };
const TYPES = [["miles", "Chauffeur MILES"], ["independent", "Indépendant"], ["partner", "Chauffeur partenaire"], ["partner_company", "Société partenaire"]];
const DOC_TYPES = ["permis", "autorisation", "assurance", "carte professionnelle"];

export function DriverForm({ initial, onSaved, onClose }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(initial || {
    first_name: "", last_name: "", phone: "", email: "", address: "",
    type: "independent", status: "available", photo_url: "", documents: [], internal_notes: "",
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const photoSrc = photoFile
    ? URL.createObjectURL(photoFile)
    : (form.photo_url ? `${process.env.REACT_APP_BACKEND_URL || ""}${form.photo_url}` : null);

  const uploadPhoto = async (file, driverId) => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await adminApi.post(`/drivers/${driverId}/photo`, fd);
    return r.data.photo_url;
  };

  const onPhotoPicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!initial?.id) { setPhotoFile(file); return; } // nouveau chauffeur : upload après création
    setPhotoBusy(true);
    try {
      const url = await uploadPhoto(file, initial.id);
      setForm((f) => ({ ...f, photo_url: url }));
      setPhotoFile(null);
      toast.success("Photo de profil mise à jour");
    } catch (error) {
      toast.error(apiError(error, "Upload impossible"));
    } finally { setPhotoBusy(false); }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const payload = { ...form, email: form.email || null, address: form.address || null, photo_url: form.photo_url || null };
      if (initial?.id) {
        await adminApi.patch(`/drivers/${initial.id}`, payload);
      } else {
        const r = await adminApi.post("/drivers", payload);
        if (photoFile && r.data?.driver?.id) {
          try { await uploadPhoto(photoFile, r.data.driver.id); } catch { toast.error("Chauffeur créé, mais la photo n'a pas pu être envoyée — réessayez depuis sa fiche."); }
        }
      }
      toast.success(initial?.id ? "Chauffeur mis à jour" : "Chauffeur créé");
      onSaved();
    } catch (error) {
      toast.error(apiError(error, "Erreur"));
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="driver-form">
      <div className="flex items-center gap-4 rounded-xl border border-slate/15 p-3" data-testid="driver-photo-block">
        <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full border border-slate/15 bg-cloud text-lg font-bold text-miles">
          {photoSrc
            ? <img src={photoSrc} alt="Photo du chauffeur" className="h-full w-full object-cover" data-testid="driver-photo-preview" />
            : <span data-testid="driver-photo-placeholder">{(form.first_name?.[0] || "?")}{(form.last_name?.[0] || "")}</span>}
        </div>
        <div>
          <p className="text-sm font-semibold text-ink">Photo de profil</p>
          <p className="mt-0.5 text-xs text-slate">Visible par le client sur la page de suivi. JPG, PNG ou WebP — 5 Mo max.</p>
          <label className={`${btnSecondary} mt-2 inline-flex cursor-pointer items-center gap-2 ${photoBusy ? "pointer-events-none opacity-50" : ""}`} data-testid="driver-photo-button">
            {photoBusy ? "Envoi en cours…" : (photoSrc ? "Changer la photo" : "Choisir une photo")}
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="driver-photo-input" onChange={onPhotoPicked} />
          </label>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="text-xs font-medium text-slate">Prénom *</label>
          <input required value={form.first_name} onChange={set("first_name")} data-testid="driver-first-name" className={`${inputCls} mt-1`} /></div>
        <div><label className="text-xs font-medium text-slate">Nom *</label>
          <input required value={form.last_name} onChange={set("last_name")} data-testid="driver-last-name" className={`${inputCls} mt-1`} /></div>
        <div><label className="text-xs font-medium text-slate">Téléphone *</label>
          <input required value={form.phone} onChange={set("phone")} data-testid="driver-phone" className={`${inputCls} mt-1 font-mono`} /></div>
        <div><label className="text-xs font-medium text-slate">E-mail</label>
          <input type="email" value={form.email || ""} onChange={set("email")} data-testid="driver-email" className={`${inputCls} mt-1`} /></div>
        <div><label className="text-xs font-medium text-slate">Type</label>
          <select value={form.type} onChange={set("type")} data-testid="driver-type" className={`${inputCls} mt-1`}>
            {TYPES.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
          </select></div>
        <div><label className="text-xs font-medium text-slate">Statut</label>
          <select value={form.status} onChange={set("status")} data-testid="driver-status" className={`${inputCls} mt-1`}>
            {Object.entries(STATUS_LABELS).map(([id, label]) => <option key={id} value={id}>{label}</option>)}
          </select></div>
      </div>
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate">Documents</p>
        {(form.documents || []).map((doc, i) => (
          <div key={i} className="mb-2 flex items-center gap-2">
            <select value={doc.type} data-testid={`doc-type-${i}`} className={`${inputCls} w-44`}
              onChange={(e) => setForm((f) => { const docs = [...f.documents]; docs[i] = { ...docs[i], type: e.target.value }; return { ...f, documents: docs }; })}>
              {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <input type="date" value={doc.expires_at || ""} data-testid={`doc-expiry-${i}`} className={inputCls}
              onChange={(e) => setForm((f) => { const docs = [...f.documents]; docs[i] = { ...docs[i], expires_at: e.target.value }; return { ...f, documents: docs }; })} />
            <button type="button" aria-label="Supprimer" data-testid={`doc-remove-${i}`}
              onClick={() => setForm((f) => ({ ...f, documents: f.documents.filter((_, j) => j !== i) }))}>
              <X className="h-4 w-4 text-slate" strokeWidth={1.5} />
            </button>
          </div>
        ))}
        <button type="button" className={btnSecondary} data-testid="doc-add"
          onClick={() => setForm((f) => ({ ...f, documents: [...(f.documents || []), { type: "permis", expires_at: "" }] }))}>
          <Plus className="h-4 w-4" strokeWidth={2} /> Document
        </button>
      </div>
      <div className="flex gap-2 pt-2">
        <button type="submit" disabled={busy} className={btnPrimary} data-testid="driver-save">{busy ? "Enregistrement…" : "Enregistrer"}</button>
        <button type="button" onClick={onClose} className={btnSecondary}>Annuler</button>
      </div>
      {initial?.id && (
        <div className="mt-4 border-t border-ink/[0.07] pt-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-red-500/80">Zone sensible</p>
          <DeleteButton testId="driver-delete" label="Supprimer ce chauffeur"
            onConfirm={() => adminApi.delete(`/drivers/${initial.id}`)}
            onDeleted={() => onSaved()} />
          <p className="mt-2 text-xs text-slate">Impossible si le chauffeur a des courses à venir — réassignez-les d'abord.</p>
        </div>
      )}
    </form>
  );
}

export default function AdminDrivers() {
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState(null);
  const [drawer, setDrawer] = useState(false);

  const load = useCallback(() => {
    adminApi.get("/drivers").then((r) => setDrivers(r.data.drivers)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="admin-drivers">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={T.pageTitle}>Chauffeurs</h1>
          <p className="mt-1 text-sm text-slate">Chauffeurs MILES, indépendants et partenaires.</p>
        </div>
        <button type="button" onClick={() => setDrawer(true)} className={btnPrimary} data-testid="drivers-new-button">
          <Plus className="h-4 w-4" strokeWidth={2} /> Chauffeur
        </button>
      </div>

      <Panel testId="drivers-list">
        {!drivers && <Skeleton rows={3} />}
        {drivers?.length === 0 && (
          <div className="py-10 text-center" data-testid="drivers-empty">
            <p className="text-sm font-medium text-ink">Aucun chauffeur</p>
            <p className="mt-1 text-[13px] text-slate">Créez le premier pour l’attribuer aux courses.</p>
            <button type="button" onClick={() => setDrawer(true)} className="mt-3 text-sm font-semibold text-miles hover:underline">Ajouter un chauffeur</button>
          </div>
        )}
        <div className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
          {drivers?.map((d) => (
            <button key={d.id} type="button" onClick={() => navigate(`/admin/app/drivers/${d.id}`)} data-testid={`driver-card-${d.id}`}
              className="rounded-xl bg-ink/[0.025] p-4 text-left transition-colors hover:bg-miles-ice/50">
              <div className="flex items-center justify-between">
                <p className="text-[15px] font-semibold text-ink">{d.first_name} {d.last_name}</p>
                <StatusBadge status={d.status} />
              </div>
              <p className="mt-1 font-mono text-[13px] text-slate">{d.phone}</p>
              <div className="mt-3 flex justify-between font-mono text-xs text-slate">
                <span>{d.rides_total} course(s)</span>
                <span className="text-ink">{d.rides_upcoming} à venir</span>
              </div>
            </button>
          ))}
        </div>
      </Panel>

      {drawer && (
        <div className="fixed inset-0 z-50" data-testid="driver-drawer">
          <div className="absolute inset-0 bg-ink/50" onClick={() => setDrawer(false)} />
          <div className="absolute inset-y-0 right-0 w-full max-w-md overflow-auto bg-white p-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-ink">Nouveau chauffeur</h2>
              <button type="button" onClick={() => setDrawer(false)} aria-label="Fermer"><X className="h-5 w-5 text-slate" strokeWidth={1.5} /></button>
            </div>
            <DriverForm onSaved={() => { setDrawer(false); load(); }} onClose={() => setDrawer(false)} />
          </div>
        </div>
      )}
    </div>
  );
}
